import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { FunctionComponent } from 'react';

import { BannerAttention, BannerSize, BannerStyle } from '../../../../../typings/enums';
import useInitEmailSetup from '../../../../hooks/api/auth/mfa/useInitEmailSetup';
import useResendEmailSetupCode from '../../../../hooks/api/auth/mfa/useResendEmailSetupCode';
import useVerifyEmailSetup from '../../../../hooks/api/auth/mfa/useVerifyEmailSetup';
import { BUTTON_TYPE } from '../../../generic';
import Banner from '../../../generic/Banner';
import Button from '../../../generic/Button/Button';
import Flex from '../../../generic/Flex';
import VerificationCodeInput from '../../../generic/Forms/VerificationCodeInput';
import Loader from '../../../generic/Loader';

type Props = {
  show: boolean;
  onHide: () => void;
};

type SetupStep = 'verify' | 'success';

/**
 * Modal for setting up Email-based MFA.
 * Sends verification code to user's email and confirms setup.
 */
const EmailSetupModal: FunctionComponent<Props> = ({ show, onHide }) => {
  const [step, setStep] = useState<SetupStep>('verify');
  const [verificationCode, setVerificationCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const initMutation = useInitEmailSetup();
  const verifyMutation = useVerifyEmailSetup();
  const resendMutation = useResendEmailSetupCode();

  const handleModalShow = async () => {
    // Send initial code when modal opens
    setStep('verify');
    setVerificationCode('');
    try {
      await initMutation.mutateAsync();
    } catch (error) {
      // Error toast handled by useToastedMutation
      onHide();
    }
  };

  const handleVerify = async (codeValue?: string) => {
    // Use provided code value or fall back to state (for button clicks)
    const code = codeValue || verificationCode;

    // Validate code length before sending
    if (code.length !== 6) {
      return;
    }

    try {
      await verifyMutation.mutateAsync({ code });
      // Success toast handled by useToastedMutation
      setStep('success');
    } catch (error) {
      // Error toast handled by useToastedMutation
      // Clear the input on failure so user can try again
      setVerificationCode('');
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) {
      return;
    }

    try {
      await resendMutation.mutateAsync();
      // Start 60-second cooldown
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      // Error toast handled by useToastedMutation
    }
  };

  const handleClose = () => {
    setStep('verify');
    setVerificationCode('');
    setResendCooldown(0);
    onHide();
  };

  const isLoading = initMutation.isPending || verifyMutation.isPending || resendMutation.isPending;

  return (
    <Modal show={show} onHide={handleClose} onEnter={handleModalShow}>
      <Modal.Header closeButton>
        <Modal.Title>Set Up Email Verification</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && <Loader />}

        {!isLoading && step === 'verify' && (
          <div>
            <Banner
              body="A 6-digit verification code has been sent to your email address."
              bannerStyle={BannerStyle.Info}
              bannerSize={BannerSize.Small}
              bannerAttention={BannerAttention.Low}
              title="Enter the verification code from your email to complete setup."
            />

            <div className="margin-top-lg margin-bottom-lg">
              <VerificationCodeInput
                length={6}
                value={verificationCode}
                onChange={setVerificationCode}
                onComplete={handleVerify}
                disabled={isLoading}
              />
            </div>
            <div className="text-center">
              <Button
                type={BUTTON_TYPE.TEXT}
                onClick={handleResend}
                isDisabled={resendCooldown > 0}
              >
                {resendCooldown > 0
                  ? `Resend code (${resendCooldown}s)`
                  : 'Resend code'}
              </Button>
            </div>
          </div>
        )}

        {!isLoading && step === 'success' && (
          <Banner
            body=""
            bannerStyle={BannerStyle.Success}
            bannerSize={BannerSize.Small}
            bannerAttention={BannerAttention.Low}
            title="Email MFA setup complete! Your account is now more secure."
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'verify' && (
          <Flex gap="4px" justify="flex-end">
            <Button type={BUTTON_TYPE.OUTLINE} onClick={handleClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button
              type={BUTTON_TYPE.PRIMARY}
              onClick={() => handleVerify()}
              isDisabled={isLoading || verificationCode.length !== 6}
            >
              Verify and Complete Setup
            </Button>
          </Flex>
        )}
        {step === 'success' && (
          <Button type={BUTTON_TYPE.PRIMARY} onClick={handleClose}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default EmailSetupModal;
